<script setup lang="ts">
// Editing a file of an encrypted library: where it sits (path) and the fields that fit its
// kind (HRTH-164.3): music has title, artist, album, track and credits; video a title,
// release date and description; photos a title and when they were taken. Which collection
// a file is in is chosen from its ⋯ (HRTH-164.6). Tags of other kinds are kept. The new
// metadata is encrypted on this device.
import { reactive, ref } from 'vue'
import { describeError } from '../api/http'
import type { LibraryEntry } from '../api/types'
import { encryptEntryMeta, validReleaseDate, type EntryMeta } from '../lib/libraryEntries'
import { libraryPath } from '../lib/libraryUpload'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ library: string; entry: LibraryEntry; meta: EntryMeta }>()
const emit = defineEmits<{ close: []; saved: [] }>()
const session = useSession()

const t = props.meta.tags ?? {}
const list = (v?: string[]) => (v ?? []).join(', ')
const form = reactive({
  path: props.meta.path,
  title: t.title ?? '',
  artist: t.artist ?? '',
  album: t.album ?? '',
  track: t.track ? String(t.track) : '',
  release_date: t.release_date ?? '',
  label: t.label ?? '',
  composers: list(t.composers),
  producers: list(t.producers),
  description: t.description ?? '',
  taken: t.taken ?? '',
})
const kind = props.meta.kind
const busy = ref(false)
const error = ref<string | null>(null)

const split = (v: string) =>
  v
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)

async function save(): Promise<void> {
  error.value = null
  const path = libraryPath(form.path)
  if (!path) return void (error.value = 'Give the file a name.')
  for (const d of [form.release_date, form.taken]) {
    if (d.trim() && !validReleaseDate(d.trim())) return void (error.value = 'Dates look like 1977, 1977-05 or 1977-05-25.')
  }
  const track = form.track.trim() ? Number(form.track) : undefined
  if (track !== undefined && (!Number.isInteger(track) || track < 0 || track > 9999)) {
    return void (error.value = 'Track numbers go from 0 to 9999.')
  }
  const key = session.libraryKey(props.library)
  if (!key) return void (error.value = 'This device doesn’t have this library’s key.')
  // Start from what's there, so tags this form doesn't show for the kind are kept.
  const tags: NonNullable<EntryMeta['tags']> = { ...(props.meta.tags ?? {}) }
  type TextTag = 'title' | 'artist' | 'album' | 'release_date' | 'label' | 'taken'
  const text = (k: TextTag, max = 300) => {
    const v = form[k].trim()
    if (v) tags[k] = v.slice(0, max)
    else delete tags[k]
  }
  const shown: Record<string, TextTag[]> = {
    audio: ['title', 'artist', 'album', 'release_date', 'label'],
    video: ['title', 'release_date'],
    image: ['title', 'taken'],
  }
  for (const k of shown[kind] ?? []) text(k)
  if (kind === 'audio') {
    if (track !== undefined) tags.track = track
    else delete tags.track
    if (split(form.composers).length) tags.composers = split(form.composers)
    else delete tags.composers
    if (split(form.producers).length) tags.producers = split(form.producers)
    else delete tags.producers
  }
  if (kind === 'video') {
    const d = form.description.trim()
    if (d) tags.description = d.slice(0, 2000)
    else delete tags.description
  }
  const meta: EntryMeta = { ...props.meta, path }
  if (Object.keys(tags).length) meta.tags = tags
  else delete meta.tags
  busy.value = true
  try {
    await session.api.updateLibraryEntry(props.entry.id, await encryptEntryMeta(key, props.library, meta))
    emit('saved')
    emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal title="Edit file" @close="emit('close')">
    <form class="space-y-3" data-testid="entry-edit" @submit.prevent="save">
      <div>
        <label class="label" for="entry-path">Folder and name</label>
        <input id="entry-path" v-model="form.path" class="input" maxlength="1000" data-testid="entry-path" />
      </div>
      <div v-if="kind === 'audio' || kind === 'video' || kind === 'image'">
        <label class="label" for="entry-title">Title</label>
        <input id="entry-title" v-model="form.title" class="input" maxlength="300" data-testid="entry-title" />
      </div>
      <template v-if="kind === 'audio'">
        <div class="grid gap-3 sm:grid-cols-2">
          <div>
            <label class="label" for="entry-artist">Artist</label>
            <input id="entry-artist" v-model="form.artist" class="input" maxlength="300" data-testid="entry-artist" />
          </div>
          <div>
            <label class="label" for="entry-album">Album</label>
            <input id="entry-album" v-model="form.album" class="input" maxlength="300" />
          </div>
          <div>
            <label class="label" for="entry-track">Track</label>
            <input id="entry-track" v-model="form.track" class="input" inputmode="numeric" maxlength="4" />
          </div>
          <div>
            <label class="label" for="entry-release">Released</label>
            <input id="entry-release" v-model="form.release_date" class="input" placeholder="1977-05-25" maxlength="10" />
          </div>
          <div class="sm:col-span-2">
            <label class="label" for="entry-label">Label</label>
            <input id="entry-label" v-model="form.label" class="input" maxlength="300" />
          </div>
        </div>
        <div>
          <label class="label" for="entry-composers">Composers</label>
          <input id="entry-composers" v-model="form.composers" class="input" placeholder="Separate with commas" />
        </div>
        <div>
          <label class="label" for="entry-producers">Producers</label>
          <input id="entry-producers" v-model="form.producers" class="input" placeholder="Separate with commas" />
        </div>
      </template>
      <template v-else-if="kind === 'video' || kind === 'image'">
        <div class="grid gap-3 sm:grid-cols-2">
          <div v-if="kind === 'video'">
            <label class="label" for="entry-release">Released</label>
            <input id="entry-release" v-model="form.release_date" class="input" placeholder="2024-05-25" maxlength="10" />
          </div>
          <div v-else>
            <label class="label" for="entry-taken">Taken</label>
            <input id="entry-taken" v-model="form.taken" class="input" placeholder="2026-07-14" maxlength="10" data-testid="entry-taken" />
          </div>
        </div>
        <div v-if="kind === 'video'">
          <label class="label" for="entry-description">Description</label>
          <textarea id="entry-description" v-model="form.description" class="input min-h-24" maxlength="2000" data-testid="entry-description" />
        </div>
      </template>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="busy" data-testid="entry-save">{{ busy ? 'Saving…' : 'Save' }}</button>
      </div>
    </form>
  </Modal>
</template>
